// PreviewOnly.tsx - Presentational component for BUSINESS preview-only mode in PDFEditor
import React, { useEffect, useRef, useState } from 'react';
import PreviewSection from './components/PreviewSection';
import PDFDownloader from './components/PDFDownloader';
import ChangeLogSidebar from './components/ChangeLogSidebar';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { FiEye, FiDownload, FiMoreHorizontal, FiFileText } from 'react-icons/fi';
import { hasZeroFindings, getAvailablePreviewModes } from './utils/findingsUtils';
import { isAttestationEnabled } from './utils/attestationUtils';
import useUserCredentials from '../../utils/hooks/user/useUserCredentials';
import { UserRole } from '../../utils/api/endpoints/user/credentials';
import { getVantaStatus } from '../../utils/api/endpoints/vanta';

// Ensure Buffer is available for PDF rendering
import { Buffer } from 'buffer';
if (typeof window !== 'undefined' && !(window as any).Buffer) {
  (window as any).Buffer = Buffer;
}

interface PreviewOnlyProps {
  reportData: any;
  currentData: any;
  openFullView: () => void;
  previewMode: string;
  setPreviewMode: (mode: string) => void;
  previewHtml: string;
  previewRef: React.RefObject<HTMLDivElement>;
  isFullViewOpen: boolean;
  closeFullView: () => void;
  notification: any;
  hideNotification: () => void;
  isChangeLogOpen: boolean;
  closeChangeLog: () => void;
  changeLog: any[];
  changeLogLoading: boolean;
  previewLoading: boolean;
  onBusinessApprove?: () => void;
  onBusinessRequestChanges?: () => void;
  businessActionLoading?: boolean;
  isBusinessUser?: boolean;
  onUploadToVanta?: () => void;
  vantaUploadLoading?: boolean;
  vantaUploadProgress?: number;
}

const PreviewOnly: React.FC<PreviewOnlyProps> = ({
  reportData,
  currentData,
  openFullView,
  previewMode,
  setPreviewMode,
  previewHtml,
  previewRef,
  isFullViewOpen,
  closeFullView,
  notification,
  hideNotification,
  isChangeLogOpen,
  closeChangeLog,
  changeLog,
  changeLogLoading,
  previewLoading,
  onBusinessApprove,
  onBusinessRequestChanges,
  businessActionLoading,
  isBusinessUser,
  onUploadToVanta,
  vantaUploadLoading,
  vantaUploadProgress
}) => {
  const businessStatus = reportData?.status;
  const isTemplate2 = currentData?.template === 'template-2' || reportData?.template === 'template-2';
  const isLoaEnabled = isAttestationEnabled(currentData || reportData);
  const showAttestationDownload = isLoaEnabled && ['approved', 'report_updated', 'business_review', 'business_approved'].includes(businessStatus);
  const businessActionVisibleStatuses = [
    "approved",
    "report_updated",
    "business_review",
    "business_approved",
    "business_requested_changes"
  ];
  const showBusinessActions = isBusinessUser && businessStatus ? businessActionVisibleStatuses.includes(businessStatus) : false;
  const isBusinessApproved = businessStatus === 'business_approved';
  const isBusinessRequestedChanges = businessStatus === 'business_requested_changes';
  const businessActionsLocked = isBusinessApproved || isBusinessRequestedChanges;
  const businessActionsTooltip = isBusinessApproved
    ? 'Request has been approved'
    : isBusinessRequestedChanges
      ? 'Changes have been requested'
      : '';
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const isZeroFindings = hasZeroFindings(reportData);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [previewInstanceKey, setPreviewInstanceKey] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const pdfDownloaderRef = useRef<any>(null);
  const attestationDownloaderRef = useRef<any>(null);
  const previewSectionRef = useRef<any>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingAttestation, setIsDownloadingAttestation] = useState(false);
  const { id: userId, role } = useUserCredentials();
  const [vantaConnected, setVantaConnected] = useState(false);

  useEffect(() => {
    let mounted = true;
    const checkStatus = async () => {
      try {
        if (role === UserRole.BUSINESS && userId) {
          const status = await getVantaStatus(userId);
          const connected = (status?.vanta_connected === true) || (status?.vanta_connected === 1) || (status?.connected === true);
          if (mounted) setVantaConnected(!!connected);
        } else {
          if (mounted) setVantaConnected(false);
        }
      } catch {
        if (mounted) setVantaConnected(false);
      }
    };
    checkStatus();
    return () => { mounted = false; };
  }, [role, userId]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActionsMenuOpen(false);
      }
    };
    if (actionsMenuOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [actionsMenuOpen]);

  const handleApproveClick = () => setShowApproveModal(true);
  const handleRequestClick = () => setShowRequestModal(true);
  const handleApproveConfirm = () => {
    setShowApproveModal(false);
    onBusinessApprove && onBusinessApprove();
  };
  const handleRequestConfirm = () => {
    setShowRequestModal(false);
    // Optionally focus chat or scroll to chat section
    onBusinessRequestChanges && onBusinessRequestChanges();
  };

  const handleDownloadClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      if (previewSectionRef.current && previewSectionRef.current.downloadPDF) {
        await previewSectionRef.current.downloadPDF();
      } else {
        // Fallback to original selector-based approach if ref is not ready
        const downloadBtn = document.querySelector('button[title="Download PDF Report"]') as HTMLButtonElement;
        if (downloadBtn) {
          downloadBtn.click();
        }
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAttestationClick = async () => {
    if (isDownloadingAttestation) return;
    setIsDownloadingAttestation(true);
    try {
      if (attestationDownloaderRef.current && attestationDownloaderRef.current.downloadPDF) {
        await attestationDownloaderRef.current.downloadPDF();
      }
    } catch (error) {
      console.error('Error downloading attestation:', error);
    } finally {
      setIsDownloadingAttestation(false);
    }
  };

  const handleUploadToVantaClick = () => {
    if (!vantaConnected || !onUploadToVanta || vantaUploadLoading) return;
    onUploadToVanta();
  };

  const handleRefreshPreview = () => {
    setPreviewInstanceKey(prev => prev + 1);
    setActionsMenuOpen(false);
      };

  
  const renderModeSelector = () => {
    const availableModes = getAvailablePreviewModes(currentData || reportData);
    const modeLabels: Record<'executive' | 'full' | 'technical', string> = {
      executive: 'Executive Summary',
      technical: 'Technical Summary',
      full: 'Full Report'
    };

    return (
      <div className="flex w-full max-w-[860px] overflow-hidden rounded-[12px] border border-[#010D2C] divide-x divide-[#010D2C]">
        {availableModes.map(mode => (
          <button
            key={mode}
            onClick={() => {
              if (previewMode !== mode) {
                setPreviewMode(mode);
                setPreviewInstanceKey(prev => prev + 1);
              }
            }}
            className={`flex-1 inline-flex items-center justify-center gap-1 px-8 py-2 text-sm font-normal transition-all whitespace-nowrap ${
              previewMode === mode
                ? 'bg-[#0A1029] text-[#58CC02]'
                : 'bg-[#F3F4F6] text-[#010D2C] hover:bg-[#e7e9ee]'
            }`}
          >
            <FiFileText className={`w-3 h-3 ${previewMode === mode ? 'text-[#58CC02]' : 'text-[#010D2C]'}`} />
            {modeLabels[mode as 'executive' | 'full' | 'technical']}
          </button>
        ))}
      </div>
    );
  };

  return (
      <div className="min-h-screen bg-[#F4F4F4]">
        {showAttestationDownload && (
          <div style={{ display: 'none' }}>
            <PDFDownloader
              ref={attestationDownloaderRef}
              reportData={currentData || reportData}
              fileName={`${((currentData || reportData)?.company_name || 'Client').replace(/[^a-zA-Z0-9_-]/g, '_')}-Letter-of-Attestation.pdf`}
              previewMode="attestation"
            />
          </div>
        )}
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-[12px] shadow-sm px-8 py-3 flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2 text-slate-600 font-medium">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-[#0A1029]">
              <FiEye className="w-5 h-5" />
            </div>
            <span className="text-base">Preview</span>
          </div>
          <div className="flex items-center gap-3">
            {renderModeSelector()}
                        <button
              onClick={handleDownloadClick}
              disabled={isDownloading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[12px] border border-[#E5E7EB] text-sm font-semibold text-slate-700 hover:border-slate-300 hover:text-slate-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isDownloading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-700" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <FiDownload className="w-4 h-4" />
                  Download
                </>
              )}
            </button>
            {showAttestationDownload && (
              <button
                onClick={handleDownloadAttestationClick}
                disabled={isDownloadingAttestation}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[12px] border border-[#E5E7EB] text-sm font-semibold text-slate-700 hover:border-slate-300 hover:text-slate-900 transition disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isDownloadingAttestation ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-slate-700" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Downloading...
                  </>
                ) : (
                  <>
                    <FiDownload className="w-4 h-4" />
                    Download Letter of Attestation
                  </>
                )}
              </button>
            )}
            <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setActionsMenuOpen(prev => !prev)}
                  className="w-10 h-10 flex items-center justify-center rounded-[12px] border border-slate-200 text-slate-600 hover:border-slate-300"
                >
                <FiMoreHorizontal className="w-5 h-5" />
              </button>
              {actionsMenuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-slate-200 rounded-2xl shadow-lg py-1 z-20">
                  <button
                    onClick={handleRefreshPreview}
                    className="w-full px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-50 rounded-2xl"
                  >
                    Refresh preview
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

            {showBusinessActions && (
            <div className="bg-[#111827] rounded-[12px] px-8 py-4 text-white flex flex-wrap items-center justify-between gap-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="relative group">
                  <button
                    className={`inline-flex items-center gap-2 px-6 py-2 rounded-[12px] text-white text-sm font-semibold shadow transition ${
                      businessActionsLocked
                        ? 'bg-white/10 border border-white/20 text-white/60 cursor-not-allowed'
                        : 'bg-[#58CC02]/40 border border-[#58CC02] hover:opacity-90'
                    } ${businessActionLoading ? 'opacity-70' : ''}`}
                    onClick={handleApproveClick}
                    disabled={businessActionLoading || businessActionsLocked}
                  >
                    <img src="/icons/approve_repo.svg" alt="Approve icon" className="w-4 h-4" />
                    {businessActionLoading ? 'Approving...' : 'Approve'}
                  </button>
                  {businessActionsLocked && businessActionsTooltip && (
                    <span className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/80 px-3 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      {businessActionsTooltip}
                    </span>
                  )}
                </div>
                <div className="relative group">
                  <button
                    className={`inline-flex items-center gap-2 px-6 py-2 rounded-[12px] text-white text-sm font-semibold shadow transition ${
                      businessActionsLocked
                        ? 'bg-white/10 border border-white/20 text-white/60 cursor-not-allowed'
                        : 'bg-[#991B1B] border border-[#991B1B] hover:opacity-90'
                    } ${businessActionLoading ? 'opacity-70' : ''}`}
                    onClick={handleRequestClick}
                    disabled={businessActionLoading || businessActionsLocked}
                  >
                    <img src="/icons/req_change_report.svg" alt="Request changes icon" className="w-4 h-4" />
                    {businessActionLoading ? 'Requesting...' : 'Request Changes'}
                  </button>
                  {businessActionsLocked && businessActionsTooltip && (
                    <span className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/80 px-3 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      {businessActionsTooltip}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative inline-flex group">
                  <button
                    onClick={handleUploadToVantaClick}
                    disabled={!vantaConnected || !onUploadToVanta || vantaUploadLoading}
                    className={`inline-flex items-center gap-2 px-6 py-2 rounded-[12px] text-sm font-semibold shadow transition ${
                      vantaConnected && onUploadToVanta
                        ? 'bg-[#745CFF] hover:bg-[#6149e6]'
                        : 'bg-white/10 text-white/50 cursor-not-allowed'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {vantaUploadLoading ? 'Uploading...' : 'Upload to Vanta'}
                  </button>
                  {(!vantaConnected || !onUploadToVanta) && (
                    <span className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/80 px-3 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      Connect with Vanta to enable uploads
                    </span>
                  )}
                </div>
              </div>
              </div>
            )}

          <div className="bg-[#111827] rounded-[18px] shadow-inner border border-[#1f2937] p-4">
            <PreviewSection
              ref={previewSectionRef}
              key={previewInstanceKey}
              reportData={currentData}
              initialPreviewMode={
                ['full', 'technical', 'executive'].includes(previewMode)
                  ? previewMode as 'full' | 'technical' | 'executive'
                  : undefined
              }
              onPreviewModeChange={setPreviewMode}
              onUploadToVanta={onUploadToVanta}
              vantaUploadLoading={vantaUploadLoading}
              vantaUploadProgress={vantaUploadProgress}
              hideHeader={true}
            />
          </div>
      </div>

      <ConfirmationModal
        isOpen={showApproveModal}
        title="Confirm Approval"
        description="Are you sure you want to approve this report? This action cannot be reverted."
        confirmText="Approve"
        cancelText="Cancel"
        onConfirm={handleApproveConfirm}
        onClose={() => setShowApproveModal(false)}
      />

      <ConfirmationModal
        isOpen={showRequestModal}
        title="Request Changes"
        description="To request changes, please use the chat section in the bottom right of the page to communicate your feedback to the team."
        confirmText="OK, Got it"
        cancelText="Cancel"
        onConfirm={handleRequestConfirm}
        onClose={() => setShowRequestModal(false)}
      />
    </div>
  );
};

export default PreviewOnly; 